import { toValue } from 'vue';
const getDisplayNames = (locale, opts) => (code) => {
    const names = new Intl.DisplayNames(toValue(locale), opts);
    return names.of(code);
};
export const useIntl = (locale) => {
    /**
     * Sorts the given items by making use of Intl.Collator().compare
     * @param items the items to sort
     * @param getSortCriterionFromItemFunction a function that returns the sort criterion from each item in items
     * @param collatorOptions the options passed to the constructor of Intl.Collarotor
     * @returns the sorted items
     */
    const sortIntl = (items, getSortCriterionFromItemFunction = (item) => item, collatorOptions) => [...items].sort((a, b) => Intl.Collator(toValue(locale), collatorOptions).compare(getSortCriterionFromItemFunction(a) || '', getSortCriterionFromItemFunction(b) || ''));
    /**
     * Parses a date string into a Date object.
     * Treats date-only strings (YYYY-MM-DD) as local dates to avoid timezone shifting.
     * @param date the date to parse, either a Date object or a string
     * @returns the parsed Date object
     */
    const parseDate = (date) => {
        if (date instanceof Date)
            return date;
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date.split('-').map(Number);
            const parsed = new Date(year, month - 1, day);
            if (parsed.getFullYear() !== year
                || parsed.getMonth() !== month - 1
                || parsed.getDate() !== day) {
                throw new RangeError(`Invalid ISO local date: ${date}`);
            }
            return parsed;
        }
        return new Date(date);
    };
    /**
     * Formats the given date to a string based on the current locale
     * @param date the date to format
     * @param options the options for the formatter
     * @returns the formatted date
     */
    const formatDate = (date, options = { dateStyle: 'short' }) => new Intl.DateTimeFormat(toValue(locale), options).format(parseDate(date));
    /**
     * Formats the given relative time to a string based on the current locale
     * @param value the value to format
     * @param unit the unit of the value
     * @param options the options for the formatter
     * @returns the formatted relative time
     */
    const formatRelativeTime = (value, unit, options = { numeric: 'auto' }) => new Intl.RelativeTimeFormat(toValue(locale), options).format(value, unit);
    /**
     * Formats the given date to a date time string based on the current locale
     * @param date the date to format
     * @param options the options for the formatter
     * @returns the formatted date time
     */
    const formatDateTime = (date, options = { dateStyle: 'short', timeStyle: 'short' }) => formatDate(date, options);
    return {
        sortIntl,
        formatDate,
        formatRelativeTime,
        formatDateTime,
        getLanguageName: (code, style) => getDisplayNames(locale, { type: 'language', style })(code),
        getRegionName: getDisplayNames(locale, { type: 'region' }),
    };
};
